/**
 * Schema of `_state.db`, the database replicated through Drive: deck
 * instances, FSRS review state and log, sessions and tags.
 *
 * This file holds the **baseline** (version 5) as idempotent DDL. Everything
 * after the baseline is an explicit, numbered step in `STATE_MIGRATIONS`
 * (./state-migrations.ts) — never edit the baseline to change an existing DB.
 *
 * Note on the `notes`/`note_tags`/`note_links` tables: they belong to the
 * baseline only so older app versions syncing the same `_state.db` keep
 * working. Notes are no longer replicated through this file; they live in the
 * device-local DB (see ./local-schema.ts and docs/modulos/notas.md).
 */

/** The `user_version` a database has once the baseline DDL has been applied. */
export const STATE_BASELINE_VERSION = 5

/**
 * Canonical `deck_stats` view. Day boundaries are local midnight, and the
 * buckets are disjoint: `overdue_cards` (before today) + `due_today` (today)
 * never double-count a card.
 */
export const DECK_STATS_VIEW = `
CREATE VIEW deck_stats AS
SELECT
  d.id AS deck_instance_id,
  d.card_count AS total_cards,
  COUNT(DISTINCT r.card_id) FILTER (WHERE r.state = 0)                                         AS new_cards,
  COUNT(DISTINCT r.card_id) FILTER (WHERE r.state IN (1,2,3))                                  AS learning_cards,
  COUNT(DISTINCT r.card_id) FILTER (WHERE r.state = 2 AND r.scheduled_days < 21)               AS young_cards,
  COUNT(DISTINCT r.card_id) FILTER (WHERE r.state = 2 AND r.scheduled_days >= 21)              AS mature_cards,
  COUNT(DISTINCT r.card_id) FILTER (WHERE r.state IN (1,2,3)
    AND r.due >= CAST(strftime('%s', 'now', 'localtime', 'start of day', 'utc') AS INTEGER)
    AND r.due <  CAST(strftime('%s', 'now', 'localtime', 'start of day', '+1 day', 'utc') AS INTEGER)) AS due_today,
  COUNT(DISTINCT r.card_id) FILTER (WHERE r.state IN (1,2,3)
    AND r.due <  CAST(strftime('%s', 'now', 'localtime', 'start of day', 'utc') AS INTEGER))   AS overdue_cards,
  COUNT(DISTINCT r.card_id) FILTER (WHERE r.state IN (1,2,3)
    AND r.due >= CAST(strftime('%s', 'now', 'localtime', 'start of day', '+1 day', 'utc') AS INTEGER)
    AND r.due <  CAST(strftime('%s', 'now', 'localtime', 'start of day', '+6 days', 'utc') AS INTEGER)) AS future_cards,
  COALESCE(AVG(r.stability) FILTER (WHERE r.state > 0), 0)                                     AS avg_stability,
  0                                                                                             AS avg_retention,
  CAST(AVG(r.avg_time_ms) FILTER (WHERE r.avg_time_ms IS NOT NULL) AS INTEGER)                 AS avg_time_ms,
  0                                                                                             AS streak_days,
  MAX(r.last_review)                                                                            AS last_studied_at,
  MIN(r.last_review)                                                                            AS first_studied_at,
  CAST(strftime('%s','now') AS INTEGER)                                                         AS updated_at
FROM deck_instances d
LEFT JOIN review_state r ON d.id = r.deck_instance_id
GROUP BY d.id;
`

export const STATE_DB_SCHEMA = `
CREATE TABLE IF NOT EXISTS deck_instances (
  id                TEXT    PRIMARY KEY,
  deck_id           TEXT    NOT NULL,
  deck_name         TEXT    NOT NULL,
  wflash_drive_path TEXT    NOT NULL,
  drive_file_id     TEXT    NOT NULL,
  drive_modified_at INTEGER NOT NULL,
  imported_at       INTEGER NOT NULL,
  card_count        INTEGER NOT NULL DEFAULT 0,
  card_style        TEXT,
  deck_color        TEXT
) STRICT;

CREATE TABLE IF NOT EXISTS sessions (
  id             TEXT    PRIMARY KEY,
  started_at     INTEGER NOT NULL,
  ended_at       INTEGER,
  mode           INTEGER NOT NULL,
  cards_total    INTEGER NOT NULL DEFAULT 0,
  cards_reviewed INTEGER NOT NULL DEFAULT 0,
  avg_time_ms    INTEGER,
  cal_event_id   TEXT
) STRICT;

CREATE TABLE IF NOT EXISTS review_state (
  card_id          TEXT    NOT NULL,
  deck_instance_id TEXT    NOT NULL REFERENCES deck_instances(id) ON DELETE CASCADE,
  due              INTEGER NOT NULL,
  stability        REAL    NOT NULL DEFAULT 0.0,
  difficulty       REAL    NOT NULL DEFAULT 5.0,
  elapsed_days     INTEGER NOT NULL DEFAULT 0,
  scheduled_days   INTEGER NOT NULL DEFAULT 0,
  reps             INTEGER NOT NULL DEFAULT 0,
  lapses           INTEGER NOT NULL DEFAULT 0,
  state            INTEGER NOT NULL DEFAULT 0,
  last_review      INTEGER,
  avg_time_ms      INTEGER,
  PRIMARY KEY (card_id, deck_instance_id)
) STRICT;

CREATE TABLE IF NOT EXISTS review_log (
  id                    INTEGER PRIMARY KEY AUTOINCREMENT,
  card_id               TEXT    NOT NULL,
  deck_instance_id      TEXT    NOT NULL,
  session_id            TEXT    NOT NULL REFERENCES sessions(id),
  reviewed_at           INTEGER NOT NULL,
  rating                INTEGER NOT NULL,
  time_ms               INTEGER NOT NULL,
  stability_before      REAL    NOT NULL,
  stability_after       REAL    NOT NULL,
  difficulty_before     REAL    NOT NULL,
  difficulty_after      REAL    NOT NULL,
  scheduled_days_before INTEGER NOT NULL,
  scheduled_days_after  INTEGER NOT NULL,
  FOREIGN KEY (card_id, deck_instance_id)
    REFERENCES review_state(card_id, deck_instance_id)
) STRICT;

CREATE VIEW IF NOT EXISTS deck_stats AS
SELECT d.id AS deck_instance_id, d.card_count AS total_cards, 0 AS new_cards
FROM deck_instances d;

CREATE TABLE IF NOT EXISTS tags (
  id         TEXT    PRIMARY KEY,
  name       TEXT    NOT NULL UNIQUE,
  parent_id  TEXT,
  color      TEXT    NOT NULL,
  created_at INTEGER NOT NULL
) STRICT;

CREATE TABLE IF NOT EXISTS deck_tags (
  deck_instance_id TEXT NOT NULL REFERENCES deck_instances(id) ON DELETE CASCADE,
  tag_id           TEXT NOT NULL REFERENCES tags(id) ON DELETE CASCADE,
  PRIMARY KEY (deck_instance_id, tag_id)
) STRICT;

CREATE TABLE IF NOT EXISTS session_decks (
  session_id       TEXT    NOT NULL REFERENCES sessions(id) ON DELETE CASCADE,
  deck_instance_id TEXT    NOT NULL REFERENCES deck_instances(id),
  cards_from_deck  INTEGER NOT NULL DEFAULT 0,
  PRIMARY KEY (session_id, deck_instance_id)
) STRICT;

CREATE INDEX IF NOT EXISTS idx_rs_due      ON review_state(due);
CREATE INDEX IF NOT EXISTS idx_rs_deck_due ON review_state(deck_instance_id, due);
CREATE INDEX IF NOT EXISTS idx_rs_state    ON review_state(state);
CREATE INDEX IF NOT EXISTS idx_rl_card     ON review_log(card_id, deck_instance_id);
CREATE INDEX IF NOT EXISTS idx_rl_session  ON review_log(session_id);
CREATE INDEX IF NOT EXISTS idx_rl_time     ON review_log(reviewed_at);

CREATE TABLE IF NOT EXISTS notes (
  id                TEXT    PRIMARY KEY,
  drive_file_id     TEXT,
  parent_id         TEXT    REFERENCES notes(id) ON DELETE SET NULL,
  drive_folder_id   TEXT,
  title             TEXT    NOT NULL,
  type              TEXT    NOT NULL,
  status            TEXT    NOT NULL DEFAULT 'Pending',
  domain            TEXT,
  content_cache     TEXT,
  content_synced_at INTEGER,
  drive_modified_at INTEGER,
  local_modified_at INTEGER NOT NULL,
  checksum          TEXT,
  deleted           INTEGER NOT NULL DEFAULT 0
) STRICT;

CREATE TABLE IF NOT EXISTS note_tags (
  note_id TEXT NOT NULL REFERENCES notes(id) ON DELETE CASCADE,
  tag_id  TEXT NOT NULL REFERENCES tags(id) ON DELETE CASCADE,
  PRIMARY KEY (note_id, tag_id)
) STRICT;

CREATE TABLE IF NOT EXISTS note_links (
  id            INTEGER PRIMARY KEY AUTOINCREMENT,
  from_note_id  TEXT    NOT NULL REFERENCES notes(id) ON DELETE CASCADE,
  to_note_id    TEXT    NOT NULL REFERENCES notes(id) ON DELETE CASCADE,
  link_text     TEXT    NOT NULL
) STRICT;

CREATE INDEX IF NOT EXISTS idx_notes_parent      ON notes(parent_id);
CREATE INDEX IF NOT EXISTS idx_notes_domain      ON notes(domain);
CREATE INDEX IF NOT EXISTS idx_notes_drive_file  ON notes(drive_file_id);
CREATE INDEX IF NOT EXISTS idx_note_tags_tag     ON note_tags(tag_id);
CREATE INDEX IF NOT EXISTS idx_note_links_from   ON note_links(from_note_id);
CREATE INDEX IF NOT EXISTS idx_note_links_to     ON note_links(to_note_id);
`
